import { useState } from "react";
import { Crosshair, MagnifyingGlass, PaperPlaneRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ContactForm({ t }) {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), time: "", from_location: "", to_location: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState("");

  const searchPlace = async (field) => {
    const q = form[field];
    if (!q.trim()) return;
    setSearching(field);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=my&limit=1`);
      const j = await r.json();
      if (j.length) {
        setForm((f) => ({ ...f, [field]: j[0].display_name }));
      } else {
        toast.error("Place not found");
      }
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching("");
    }
  };

  const locateMe = () => {
    if (!navigator.geolocation) {
      toast.error("GPS not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`);
          const j = await r.json();
          setForm((f) => ({ ...f, from_location: j.display_name || `${pos.coords.latitude}, ${pos.coords.longitude}` }));
        } catch {
          setForm((f) => ({ ...f, from_location: `${pos.coords.latitude}, ${pos.coords.longitude}` }));
        }
      },
      () => toast.error("Unable to get GPS location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.from_location || !form.to_location || !form.phone) {
      toast.error("From, To and Contact Number are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success(t.form_success);
      setForm({ date: new Date().toISOString().slice(0, 10), time: "", from_location: "", to_location: "", phone: "", email: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-12 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid="contact-form">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">{t.form_title}</p>
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.form_date} *</Label>
          <Input data-testid="cf-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.form_time} *</Label>
          <Input data-testid="cf-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
            className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.form_from} *</Label>
          <div className="flex gap-2">
            <Input data-testid="cf-from" value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
            <Button type="button" variant="outline" data-testid="cf-from-locate" onClick={locateMe}
              className="shrink-0 rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400" title="Find Me">
              <Crosshair size={16} />
            </Button>
            <Button type="button" variant="outline" data-testid="cf-from-search" onClick={() => searchPlace("from_location")}
              disabled={searching === "from_location"}
              className="shrink-0 rounded-sm border-input hover:bg-secondary hover:text-foreground">
              <MagnifyingGlass size={16} />
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.form_to} *</Label>
          <div className="flex gap-2">
            <Input data-testid="cf-to" value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
            <Button type="button" variant="outline" data-testid="cf-to-search" onClick={() => searchPlace("to_location")}
              disabled={searching === "to_location"}
              className="shrink-0 rounded-sm border-input hover:bg-secondary hover:text-foreground">
              <MagnifyingGlass size={16} />
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.form_phone} *</Label>
          <Input data-testid="cf-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="01X-XXX XXXX" className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.form_email}</Label>
          <Input data-testid="cf-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-sm border-input bg-background focus-visible:ring-primary" />
        </div>
      </div>
      <Button data-testid="cf-submit" type="submit" disabled={busy}
        className="mt-6 rounded-sm bg-primary px-8 py-6 text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-amber-400 hover:text-black">
        <PaperPlaneRight size={16} className="mr-2" /> {busy ? "..." : t.form_submit}
      </Button>
    </form>
  );
}
